import { useCallback, useId, useRef, type KeyboardEvent } from 'react'

export type TabItem<T extends string> = {
  id: T
  label: string
}

type Props<T extends string> = {
  tabs: ReadonlyArray<TabItem<T>>
  value: T
  onChange: (v: T) => void
  className?: string
}

/**
 * Mobile-first segmented tablist. Arrow keys move focus (APG pattern),
 * enter/space activates. Each tab's aria-controls points at a
 * `<section id="tab-panel-{id}">` the parent renders.
 */
export default function Tabs<T extends string>({ tabs, value, onChange, className }: Props<T>) {
  const rootId = useId()
  const refs = useRef<(HTMLButtonElement | null)[]>([])

  const focusAt = useCallback((i: number) => {
    const n = tabs.length
    const idx = ((i % n) + n) % n
    refs.current[idx]?.focus()
    const id = tabs[idx]?.id
    if (id) onChange(id)
  }, [tabs, onChange])

  function handleKey(e: KeyboardEvent<HTMLButtonElement>, idx: number) {
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault()
        focusAt(idx + 1)
        break
      case 'ArrowLeft':
        e.preventDefault()
        focusAt(idx - 1)
        break
      case 'Home':
        e.preventDefault()
        focusAt(0)
        break
      case 'End':
        e.preventDefault()
        focusAt(tabs.length - 1)
        break
    }
  }

  return (
    <div
      role="tablist"
      aria-label="ส่วนการตรวจทาน"
      className={
        className ??
        'inline-flex rounded-full bg-slate-100 p-1 gap-1 w-full sm:w-auto'
      }
    >
      {tabs.map((t, i) => {
        const selected = t.id === value
        return (
          <button
            key={t.id}
            ref={(el) => {
              refs.current[i] = el
            }}
            role="tab"
            id={`${rootId}-tab-${t.id}`}
            aria-selected={selected}
            aria-controls={`tab-panel-${t.id}`}
            tabIndex={selected ? 0 : -1}
            className={
              'flex-1 sm:flex-none min-w-tap px-4 py-2 rounded-full text-sm font-medium transition ' +
              (selected
                ? 'bg-white text-brand-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-900')
            }
            onClick={() => onChange(t.id)}
            onKeyDown={(e) => handleKey(e, i)}
          >
            {t.label}
          </button>
        )
      })}
    </div>
  )
}
